import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  Bell,
  Check,
  CheckCheck,
  Clock,
  Info,
  AlertCircle,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (title) => {
    if (title.toLowerCase().includes('deposit')) return DollarSign;
    if (title.toLowerCase().includes('withdrawal')) return TrendingUp;
    if (title.toLowerCase().includes('profit')) return TrendingUp;
    if (title.toLowerCase().includes('welcome')) return Bell;
    return Info;
  };

  const getNotificationColor = (title) => {
    if (title.toLowerCase().includes('approved') || title.toLowerCase().includes('completed') || title.toLowerCase().includes('profit')) return 'emerald';
    if (title.toLowerCase().includes('rejected')) return 'hotpink';
    if (title.toLowerCase().includes('pending')) return 'yellow';
    return 'cyan';
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-6"
      data-testid="notifications-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white">Notifications</h1>
          <p className="text-white/60 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button 
            onClick={markAllAsRead}
            variant="outline"
            className="btn-secondary"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-5 h-5 mr-2" />
            Mark All Read
          </Button>
        )}
      </motion.div>

      {/* Notifications List */}
      <motion.div variants={fadeInUp} className="space-y-3">
        {notifications.map((notification) => {
          const Icon = getNotificationIcon(notification.title);
          const color = getNotificationColor(notification.title);
          
          return (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`glass-card p-4 cursor-pointer transition-all ${
                !notification.is_read 
                  ? 'border-l-4 border-l-cyan-500' 
                  : 'opacity-70 hover:opacity-100'
              }`}
              onClick={() => !notification.is_read && markAsRead(notification.id)}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  color === 'emerald' ? 'bg-emerald-500/20' :
                  color === 'hotpink' ? 'bg-hotpink-400/20' :
                  color === 'yellow' ? 'bg-yellow-500/20' :
                  'bg-cyan-500/20'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    color === 'emerald' ? 'text-emerald-400' :
                    color === 'hotpink' ? 'text-hotpink-400' :
                    color === 'yellow' ? 'text-yellow-400' :
                    'text-cyan-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`font-medium ${notification.is_read ? 'text-white/70' : 'text-white'}`}>
                      {notification.title}
                    </h3>
                    {!notification.is_read && (
                      <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0 mt-2" />
                    )}
                  </div>
                  <p className="text-white/50 text-sm mt-1">{notification.message}</p>
                  <p className="text-white/30 text-xs mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
        
        {notifications.length === 0 && (
          <div className="glass-card p-12 text-center">
            <Bell className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">No notifications yet</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
